import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { IntegrationOAuthSecret } from "@prescreen/lib/types"

export type OAuthSecretUsageDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  secret: IntegrationOAuthSecret | null
}

export function OAuthSecretUsageDialog({ open, onOpenChange, secret }: OAuthSecretUsageDialogProps) {
  const usage = secret?.tokenUsage
  const total = usage?.tokensIssuedCount ?? 0
  const recent = [...(usage?.recentActivity ?? [])].sort(
    (a, b) => new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime(),
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Token usage</DialogTitle>
          <DialogDescription>
            {secret ? (
              <>
                Recent client-credentials token requests for{" "}
                <span className="font-medium text-foreground">{secret.name}</span>. Up to 50 events are
                retained per secret.
              </>
            ) : (
              "Select a secret to view usage."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Total tokens issued:{" "}
            <span className="font-semibold tabular-nums text-foreground">{total}</span>
          </p>

          <div className="max-h-[50vh] overflow-y-auto rounded-md border">
            {recent.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">No token requests recorded yet.</p>
            ) : (
              <ul className="divide-y">
                {recent.map((row, index) => (
                  <li key={`${row.issuedAt.toString()}-${index}`} className="px-3 py-2.5 text-sm">
                    <div className="font-medium text-foreground">
                      {new Date(row.issuedAt).toLocaleString()}
                    </div>
                    <div className="mt-0.5 text-muted-foreground">
                      Requested scopes:{" "}
                      <span className="tabular-nums text-foreground">{row.requestedScopeCount}</span>
                      {" · "}
                      Granted scopes:{" "}
                      <span className="tabular-nums text-foreground">{row.grantedScopeCount}</span>
                      {row.clientIp ? (
                        <>
                          {" · "}
                          IP: <span className="font-mono text-xs">{row.clientIp}</span>
                        </>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
